import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import config from '../../config';
import Header from '../Header';
import Footer from '../Footer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faMinus, faAngleDoubleLeft, faAngleDoubleRight, faEye, faEyeSlash, faSearch, faSort, faSortUp, faSortDown } from '@fortawesome/free-solid-svg-icons';
import { format } from 'date-fns';
import PopupService from '../PopupService';
import { useApi } from '../../ApiContext';
import { apiFetch } from '../utils/ApiFetch';
import SearchComponent from '../matching/SearchComponent';

const Home = () => {
	const [activeTab, setActiveTab] = useState('table');
	const [mrNumber, setMrNumber] = useState('');
	const [suggestions, setSuggestions] = useState([]);
	const { callApi, setIsApiLoading } = useApi();
	const [showSuggestions, setShowSuggestions] = useState(false);
	const [mrNumberTitle, setMrNumberTitle] = useState('');
	const [showMrNumberTitle, setShowMrNumberTitle] = useState(false);
	const [showAlertDetailTab, setShowAlertDetailTab] = useState(false);
	const [alertDetail, setAlertDetail] = useState(null);
	const [attachments, setAttachments] = useState([]);
	const [tableData, setTableData] = useState([]);
	const [currentPage, setCurrentPage] = useState(1);
	const [totalPages, setTotalPages] = useState(1);
	const [totalRecords, setTotalRecords] = useState(0);
	const [loading, setLoading] = useState(false);
	const [pageLimit, setPageLimit] = useState(20);

	const [showTable, setShowTable] = useState(true);
	const [sortConfig, setSortConfig] = useState({ key: 'createTimestamp', direction: 'desc' });
	const [batchMatchCriteria, setBatchMatchCriteria] = useState(false);
	const [duplicates, setDuplicates] = useState([]);
	const [supportAlert, setSupportAlert] = useState(null);
	const [expandedRows, setExpandedRows] = useState({});
	const [rowDetails, setRowDetails] = useState({});
	const navigate = useNavigate();
	const username = sessionStorage.getItem("username");
	const mrToken = localStorage.getItem("mr_token");
	const [dropdownValue, setDropdownValue] = useState('');
	const [searchQuery, setSearchQuery] = useState('');
	const [showStatusPopup, setShowStatusPopup] = useState(false);
	const [statusRow, setStatusRow] = useState(null);
	const [selectedStatus, setSelectedStatus] = useState('NEW');
	const STATUS_OPTIONS = ['NEW', 'PENDING', 'EXPORTED', 'NOT EXPORTED', 'DISTRIBUTED', 'PROCESSED', 'MARKED_DISTRIBUTION'];
	const [statusLoading, setStatusLoading] = useState(false); const [showMarkDistribute, setShowMarkDistribute] = useState(false);
	const [mappedRouting, setMappedRouting] = useState('');
	const [unmappedRouting, setUnmappedRouting] = useState('');
	const [markDistRow, setMarkDistRow] = useState(null);
	const [highlightedMappedRouting, setHighlightedMappedRouting] = useState('');
	const [gotoPageInput, setGotoPageInput] = useState('');
	const selectedClientId = markDistRow?.clientId;
	const [showDuplicatesTab, setShowDuplicatesTab] = useState(false);
	const [comparedMrNumber, setComparedMrNumber] = useState('');
	const [comparisonResult, setComparisonResult] = useState(null);
	const searchComponentRef = useRef(null);
	const [selectedMrNumber, setSelectedMrNumber] = useState('');
	const [selectedMongoId, setSelectedMongoId] = useState('');
	const [viewMode, setViewMode] = useState('table');
	
	const topScrollRef = useRef(null);
	const tableScrollRef = useRef(null);
	const [tableScrollWidth, setTableScrollWidth] = useState(0);

	const [showDownloadModal, setShowDownloadModal] = useState(false);


	useEffect(() => {
	  const tableContainer = tableScrollRef.current;
	  const topScrollContainer = topScrollRef.current;
	  if (!tableContainer || !topScrollContainer) return;

	  const updateWidth = () => {
	    const tableEl = tableContainer.querySelector('table');
	    if (tableEl) {
	      setTableScrollWidth(tableEl.scrollWidth);
	    }
	  };

	  updateWidth();

	  const handleTopScroll = () => {
	    tableContainer.scrollLeft = topScrollContainer.scrollLeft;
	  };

	  const handleTableScroll = () => {
	    topScrollContainer.scrollLeft = tableContainer.scrollLeft;
	  };

	  topScrollContainer.addEventListener('scroll', handleTopScroll);
	  tableContainer.addEventListener('scroll', handleTableScroll);

	  const resizeObserver = new ResizeObserver(updateWidth);
	  if (tableContainer.querySelector('table')) {
	    resizeObserver.observe(tableContainer.querySelector('table'));
	  }

	  return () => {
	    topScrollContainer.removeEventListener('scroll', handleTopScroll);
	    tableContainer.removeEventListener('scroll', handleTableScroll);
	    resizeObserver.disconnect();
	  };
	}, [tableData, loading, showTable]);
	
	useEffect(() => {
		const savedPage = sessionStorage.getItem('lastPage');
		if (savedPage) {
			setCurrentPage(Number(savedPage));
			sessionStorage.removeItem('lastPage');
		}
	}, []);


	useEffect(() => {
		if (localStorage.getItem('isLoggedIn') !== 'true') {
			navigate('/');
		}
	}, [navigate]);

	useEffect(() => {
		if (activeTab === 'table') {
			fetchTableData(currentPage, pageLimit, searchQuery, sortConfig);
		}
	}, [activeTab, currentPage, pageLimit, searchQuery, sortConfig]);


	const handleGotoPage = () => {
		const page = Number(gotoPageInput);
		if (!page || page < 1 || page > totalPages) {
			PopupService.getInstance().showMessage('Entered page number not valid', false);
			return;
		}
		setCurrentPage(page);
	};

	function renderDataPairsHtml(dataPairs) {
		if (!Array.isArray(dataPairs)) return '';
		return dataPairs
			.map(dp => `<div><strong>${dp.dataName}:</strong> ${dp.dataValue}</div>`)
			.join('');
	}

	const fetchTableData = async (page, limit, searchQuery, sort = sortConfig) => {

		setLoading(true);
		await callApi(async () => {
			setIsApiLoading(true);
			try {
				const response = await apiFetch(`${config.API_BASE_URL}/configuredmatches`, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						'X-Username': username,
						'Authorization': `Bearer ${mrToken}`
					},
					body: JSON.stringify({
						pageNo: page,
						limit: limit,
						sort: sort.key || 'createTimestamp',
						sortOrder: sort.direction || 'desc',
						search: searchQuery
					}),
				});
				if (response.ok) {
					const data = await response.json();
					const list = Array.isArray(data?.data?.SEARCH_LIST) ? data.data.SEARCH_LIST : [];
					setTableData(list);
					const total = data?.data?.total || list.length;
					setTotalPages(Math.ceil(total / limit) || 1);
					setTotalRecords(total);
				} else if (response.status === 401 || response.status === 403) {
					sessionStorage.clear();
					localStorage.clear();
					window.location.href = '/login';
					return;
				}
				else {
					setTableData([]);
					setTotalPages(1);
					setTotalRecords(0);

				}
			} catch (error) {
				sessionStorage.clear();
				localStorage.clear();
				window.location.href = '/login';
			} finally {
				setLoading(false);
				setIsApiLoading(false);
			}
		});
	};



	const handleExpandRow = async (mongoId) => {
		setExpandedRows(prev => {
			if (prev[mongoId]) {
				return {};
			}
			return { [mongoId]: true };
		});

		if (!rowDetails[mongoId]) {
			setLoading(true);
			await callApi(async () => {
				setIsApiLoading(true);
				try {
					const response = await apiFetch(`${config.API_BASE_URL}/matchdetail`, {
						method: 'POST',
						headers: { 'Content-Type': 'application/json', 'X-Username': username, 'Authorization': `Bearer ${mrToken}` },
						body: JSON.stringify({ batchMatchId: mongoId }),
					});
					if (response.ok) {
						const data = await response.json();
						setRowDetails(prev => ({
							...prev,
							[mongoId]: Array.isArray(data?.data.BATCH_XLS_LIST) ? data.data.BATCH_XLS_LIST : []
						}));
						// Scroll to the expanded row after state update
						setTimeout(() => {
							const el = document.getElementById(`row_${mongoId}`);
							if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
						}, 200);
					} else {
						setRowDetails(prev => ({ ...prev, [mongoId]: [] }));
					}
				} catch {
					setRowDetails(prev => ({ ...prev, [mongoId]: [] }));
				} finally {
					setLoading(false);
					setIsApiLoading(false);
				}
			});
		}
	};


	const handleRefresh = () => {
		setCurrentPage(1);
		setSearchQuery('');
		setGotoPageInput('');
		fetchTableData(currentPage, pageLimit, searchQuery, sortConfig);
	};

	const handleToggleTable = () => {
		setShowTable(v => !v);
	};

	const loadDuplicates = async (mongoId, mrNumber) => {
		setSelectedMongoId(mongoId);
		setComparedMrNumber(mrNumber);

		try {
			const response = await apiFetch(`${config.API_BASE_URL}/duplicates`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json', 'X-Username': username, 'Authorization': `Bearer ${mrToken}` },
				body: JSON.stringify({ batchMatchId: mongoId }),
			});

			if (response.ok) {
				const data = await response.json();
				const duplicateList = Array.isArray(data?.data?.DUPLICATE_ALERT_LIST) ? data.data.DUPLICATE_ALERT_LIST : [];
				setDuplicates(duplicateList);
				setSupportAlert(data?.data?.SUPPORT_ALERT || null);
				setBatchMatchCriteria(false);
				setShowDuplicatesTab(true); // Show the tab
				setActiveTab('third');      // Switch to the third tab
			} else {
				setDuplicates([]);
				setSupportAlert(null);
				setShowDuplicatesTab(false); // Hide the tab if no data
			}
		} catch (error) {
			setDuplicates([]);
			setSupportAlert(null);
			setShowDuplicatesTab(false);
		}
	};

	async function loadMatchCriteria(mongoId) {
		setSelectedMongoId(mongoId);
		try {
			const response = await apiFetch(`${config.API_BASE_URL}/matchcriteria`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json', 'X-Username': username,
					'Authorization': `Bearer ${mrToken}`
				},
				body: JSON.stringify({ batchMatchId: mongoId }),
			});

			if (!response.ok) {
				throw new Error(`Error: ${response.statusText}`);
			}

			const data = await response.json();

			if (data.data && data.data.BATCH_MATCH_CRITERIA) {
				setBatchMatchCriteria(data.data.BATCH_MATCH_CRITERIA || null);
				setShowAlertDetailTab(true);
				setActiveTab('second');
				setAlertDetail(data?.data?.SUPPORT_ALERT || null);
				setAttachments(data?.data?.ATTACHMENT_LIST || []);
				setDuplicates([]);
				setSupportAlert(null);
			}
		} catch (error) {
			// handle error
		}
	}

	const handleSearchChange = e => {
		setSearchQuery(e.target.value);
		setCurrentPage(1);
	};

	const handleSort = key => {
		setSortConfig(prev => {
			if (prev.key === key) {
				return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
			}
			return { key, direction: 'asc' };
		});
		setCurrentPage(1);
	};

	const handleMrNumberChange = async (e) => {
		const value = e.target.value;
		setMrNumber(value);
		setShowMrNumberTitle(false);
		setMrNumberTitle('');
		setShowAlertDetailTab(false);
		setAlertDetail(null);
		setAttachments([]);
		if (value.trim().length < 8) {
			setSuggestions([]);
			setShowSuggestions(false);
			return;
		}
		try {
			const response = await apiFetch(`${config.API_BASE_URL}/mrnumbers?searchString=${encodeURIComponent(value)}`, {
				method: 'GET',
				headers: { 'Content-Type': 'application/json', 'X-Username': username, 'Authorization': `Bearer ${mrToken}` }
			});

			if (response.ok) {
				const text = await response.text();
				let data;
				try {
					data = JSON.parse(text);
				} catch (err) {
					setSuggestions([]);
					setShowSuggestions(false);
					return;
				}
				const filtered = Array.isArray(data.data)
					? data.data.filter(obj => obj && (obj.mrNumber || obj.mr_number))
					: [];
				const normalized = filtered.map(obj => ({
					mrNumber: obj.mrNumber || obj.mr_number
				}));
				setSuggestions(normalized);
				setShowSuggestions(normalized.length > 0);
			} else {
				setSuggestions([]);
				setShowSuggestions(false);
			}
		} catch (err) {
			setSuggestions([]);
			setShowSuggestions(false);
		}
	};


	const handleMrNumberClick = (mongoId, mrNumber, alertTitle) => {
		const randomNum = Math.floor(Math.random() * 1000000); // Generate random number
		sessionStorage.setItem('lastPage', currentPage);
		navigate('/matching/matchingsummary', {
			state: {
				mongoId,
				mrNumber,
				alertTitle,
				randomNum // Add random number to state
			}
		});
	};



	const handleDropdownChange = (action, row) => {
		if (!action) return;
		handleAction(action, row);
		setDropdownValue('');
	};



	const downloadProdMatchAttachment = async (mongoId) => {
	  setShowDownloadModal(true);
	  try {
	    const url = `${config.API_BASE_URL}/downloadProductMatch`;
	    const response = await apiFetch(url, {
	      method: 'POST',
	      headers: {
	        'X-Username': username,
	        'Authorization': `Bearer ${mrToken}`,
	        'Content-Type': 'application/json'
	      },
	      body: JSON.stringify({ mongoId })
	    });
	    if (!response.ok) throw new Error('Download failed');
	    const blob = await response.blob();

	    const disposition = response.headers.get('Content-Disposition');
	    let fileName = 'ProductMatch.txt';
	    if (disposition && disposition.includes('filename=')) {
	      const match = disposition.match(/filename="?([^";]+)"?/);
	      if (match && match[1]) {
	        fileName = match[1];
	      }
	    }

	    const downloadUrl = window.URL.createObjectURL(blob);
	    const a = document.createElement('a');
	    a.download = fileName;
	    a.href = downloadUrl;
	    document.body.appendChild(a);
	    a.click();
	    a.remove();
	    window.URL.revokeObjectURL(downloadUrl);

	    setShowDownloadModal(false);
	  } catch (e) {
	    setShowDownloadModal(false);
	    PopupService.getInstance().showMessage('Failed to download attachment', false);
	  }
	};




	const fetchMarkDistributionData = async (mongoId) => {
		const resp = await apiFetch(`${config.API_BASE_URL}/loadMarkDistribution`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				'X-Username': username,
				'Authorization': `Bearer ${mrToken}`
			},
			body: JSON.stringify({ mongoId })
		});
		if (!resp.ok) throw new Error('Failed to load distribution data');
		return await resp.json();
	};





	const downloadRouting = async (mongoId) => {
	  setShowDownloadModal(true);
	  try {
	   const url = `${config.API_BASE_URL}/downloadRouting`;
	   const response = await apiFetch(url, {
	    method: 'POST',
	    headers: {
	     'X-Username': username,
	     'Authorization': `Bearer ${mrToken}`,
	     'Content-Type': 'application/json'
	    },
	    body: JSON.stringify({ mongoId })
	   });
	   if (!response.ok) throw new Error('Download failed');
	   const blob = await response.blob();

	   const disposition = response.headers.get('Content-Disposition');
	   let fileName = 'Routing.xlsx';
	   if (disposition && disposition.includes('filename=')) {
	    const match = disposition.match(/filename="?([^";]+)"?/);
	    if (match && match[1]) {
	     fileName = match[1];
	    }
	   }

	   const downloadUrl = window.URL.createObjectURL(blob);
	   const a = document.createElement('a');
	   a.download = fileName;
	   a.href = downloadUrl;
	   document.body.appendChild(a);
	   a.click();
	   a.remove();
	   window.URL.revokeObjectURL(downloadUrl);

	   setShowDownloadModal(false);
	  } catch (e) {
	   setShowDownloadModal(false);
	   PopupService.getInstance().showMessage('Failed to download attachment', false);
	  }
	 };


	// Add this function inside your Home component
	const handleMarkDistribution = async (row) => {
		setShowMarkDistribute(true);
		setHighlightedMappedRouting('');
		setMarkDistRow(row);
		setIsApiLoading(true);
		try {
			const data = await fetchMarkDistributionData(row.mongoId);
			setMappedRouting(data?.data?.MAPPED_ROUTING || '');
			setUnmappedRouting(data?.data?.UNMAPPED_ROUTING || '');
			setTimeout(() => {
				const el = document.getElementById('mark_distribute');
				if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
			}, 100);
		} catch {
			PopupService.getInstance().showMessage('Failed to load distribution data', false);
			setShowMarkDistribute(false);
		} finally {
			setIsApiLoading(false);
		}
	};


	const handleAction = async (action, row) => {
		try {
			let apiUrl = '';
			switch (action) {

				case 'Download Product Match':
					downloadProdMatchAttachment(row.mongoId);
					break;

				case 'Download Routing Sheet':
					downloadRouting(row.mongoId);
					break;

				case 'Export To Tracking':
					sessionStorage.setItem('lastPage', currentPage);
					navigate('/export/exportalert', {
						state: {
							mongoId: row.mongoId,
							clientId: row.clientId,
							mrNumber: row.mrNumber,
							clientName: row.clientName
						}
					});
					break;

				case 'Mark for Distribution':
					if (row.status === 'NOT EXPORTED') {
						PopupService.getInstance().showMessage('The alert is not in EXPORTED status. It cannot be marked for distribution.', false);
						break;
					}
					handleMarkDistribution(row);
					break;

				case 'Change Status':
					setStatusRow(row);
					setSelectedStatus(row.status || 'NEW');
					setShowStatusPopup(true);
					break;
				case 'Wildcard Product Match':
					apiUrl = `/api/wildcardProductMatch/${row.id}`;
					break;
				case 'Wildcard Routing Sheet':
					apiUrl = `/api/wildcardRoutingSheet/${row.id}`;
					break;
				default:
					console.error('Unknown action:', action);

			}


		} catch (error) {
			console.error('Error performing action:', error);
			alert(`Failed to perform action "${action}"`);
		}
	};

	const handleSuggestionClick = async (selectedObj) => {
		setMrNumber(selectedObj.mrNumber);
		setSuggestions([]);
		setShowSuggestions(false);
		setShowAlertDetailTab(false);
		setAlertDetail(null);
		setAttachments([]);
		setActiveTab('second');
		setDuplicates([]);
		setSupportAlert(null);
		try {
			const response = await apiFetch(`${config.API_BASE_URL}/alertdetail`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json', 'X-Username': username, 'Authorization': `Bearer ${mrToken}` },
				body: JSON.stringify({ mrNumber: selectedObj.mrNumber }),
			});

			if (response.ok) {
				const data = await response.json();
				const alertTitle = data?.data?.SUPPORT_ALERT?.alertTitle || 'No title found';
				setMrNumberTitle(alertTitle);
				setShowMrNumberTitle(true);
				setAlertDetail(data?.data?.SUPPORT_ALERT || null);
				setAttachments(data?.data?.ATTACHMENT_LIST || []);
				if (alertTitle && alertTitle !== 'No title found') {
					setShowAlertDetailTab(true);
				}
			} else {
				setMrNumberTitle('No title found');
				setShowMrNumberTitle(true);
				setShowAlertDetailTab(false);
				setAlertDetail(null);
				setAttachments([]);
			}
		} catch {
			setMrNumberTitle('Error fetching title');
			setShowMrNumberTitle(true);
			setShowAlertDetailTab(false);
			setAlertDetail(null);
			setAttachments([]);
		}
	};

	// Pagination group logic: only 10 page numbers at a time
	const pageGroupSize = 10;
	const currentGroup = Math.floor((currentPage - 1) / pageGroupSize);
	const startPage = currentGroup * pageGroupSize + 1;
	const endPage = Math.min(startPage + pageGroupSize - 1, totalPages);
	const pageNumbers = [];
	for (let i = startPage; i <= endPage; i++) {
		pageNumbers.push(i);
	}

	const renderSortIcon = key => {
		if (sortConfig.key !== key) return <>&nbsp;&nbsp;<FontAwesomeIcon icon={faSort} /></>;
		return sortConfig.direction === 'asc'
			? <>&nbsp;&nbsp;<FontAwesomeIcon icon={faSortUp} /></>
			: <>&nbsp;&nbsp;<FontAwesomeIcon icon={faSortDown} /></>;
	};


	const addCtlgCriteria = () => {
		// Get values from DOM elements
		const operator = document.getElementById('prd-code-operator').value;
		const ctlgCrt = document.getElementById('catalog_criteria').value;
		const finalCtlgCrt = document.getElementById('final_catalog_criteria').value;
		const previousCrt = finalCtlgCrt;
		let currentCriteria = '';

		if (operator === '') {
			// Add red border and show error message
			document.getElementById('prd-code-operator').classList.add('ui-helper-redBoarder');
			// Using your existing feedback system - you may need to implement this function
			PopupService.getInstance().showMessage('Please select catalog id type', false);
		} else {
			document.getElementById('prd-code-operator').classList.remove('ui-helper-redBoarder');

			if (ctlgCrt === '') {
				document.getElementById('catalog_criteria').classList.add('ui-helper-redBoarder');
				PopupService.getInstance().showMessage('Please enter valid catalog criteria here', false);
			} else {
				document.getElementById('catalog_criteria').classList.remove('ui-helper-redBoarder');
				currentCriteria = operator + ":" + ctlgCrt;

				let newFinalCriteria;
				if (previousCrt === '') {
					newFinalCriteria = currentCriteria;
				} else {
					newFinalCriteria = currentCriteria + " " + previousCrt;
				}

				// Update fields
				document.getElementById('prd-code-operator').value = '';
				document.getElementById('catalog_criteria').value = '';
				document.getElementById('final_catalog_criteria').value = newFinalCriteria;
			}
		}
	};



	const clearCtlgCriteria = () => {
		// Clear the select dropdown
		document.getElementById('prd-code-operator').value = '';

		// Clear the catalog criteria textarea
		document.getElementById('catalog_criteria').value = '';

		// Clear the final catalog criteria textarea
		document.getElementById('final_catalog_criteria').value = '';
	};





	const addNewMatchingCriteria = async () => {
	  // Get form values
	  const mrNbr = document.getElementById('mr_number').value.trim();
	  const prdCrt = document.getElementById('product_criteria').value.trim().toLowerCase();
	  const mfrCrt = document.getElementById('manufacturer_criteria').value.trim().toLowerCase();
	  const ctlgCrt = document.getElementById('final_catalog_criteria').value.trim().toLowerCase();

	  // Reset border styling
	  ['mr_number', 'product_criteria', 'manufacturer_criteria', 'final_catalog_criteria'].forEach(id => {
	    document.getElementById(id)?.classList.remove('ui-helper-redBoarder');
	  });

	  // Count non-empty criteria fields
	  const filledCriteriaCount = [prdCrt, mfrCrt, ctlgCrt].filter(Boolean).length;

	  let isValid = true;
	  let errorMessage = '';

	  // Validate MR Number
	  if (!mrNbr) {
	    isValid = false;
	    document.getElementById('mr_number').classList.add('ui-helper-redBoarder');
	    errorMessage = 'Please enter MR Number.';
	  } else if (!mrNbr.startsWith('MR-')) {
	    isValid = false;
	    document.getElementById('mr_number').classList.add('ui-helper-redBoarder');
	    errorMessage = 'MR Number must start with MR-.';
	  }

	  // Validate that at least two of the three criteria are provided
	  if (filledCriteriaCount < 2) {
	    isValid = false;
	    if (!prdCrt) document.getElementById('product_criteria').classList.add('ui-helper-redBoarder');
	    if (!mfrCrt) document.getElementById('manufacturer_criteria').classList.add('ui-helper-redBoarder');
	    if (!ctlgCrt) document.getElementById('final_catalog_criteria').classList.add('ui-helper-redBoarder');
	    if (!errorMessage) {
	      errorMessage = 'Please provide at least two of Product, Manufacturer, or Catalog criteria.';
	    }
	  }

	  if (isValid) {
	    setIsApiLoading(true);

	    try {
	      const response = await apiFetch(`${config.API_BASE_URL}/addBatchMatchCriteria`, {
	        method: 'POST',
	        headers: {
	          'Content-Type': 'application/json',
	          'X-Username': username,
	          'Authorization': `Bearer ${mrToken}`
	        },
	        body: JSON.stringify({
	          mrNumber: mrNbr,
	          productCriteria: prdCrt,
	          mfgCriteria: mfrCrt,
	          catalogCriteria: ctlgCrt,
	          username: username
	        })
	      });

	      const data = await response.json();

	      if (data.message) {
	        PopupService.getInstance().showMessage(data.message, true);

	        // Clear form
	        document.getElementById('mr_number').value = '';
	        document.getElementById('product_criteria').value = '';
	        document.getElementById('manufacturer_criteria').value = '';
	        document.getElementById('catalog_criteria').value = '';
	        document.getElementById('final_catalog_criteria').value = '';
	        document.getElementById('prd-code-operator').value = '';

	        // Clear state
	        setMrNumber('');
	        setMrNumberTitle('');
	        setShowMrNumberTitle(false);

	        // Switch to table tab and refresh data
	        setActiveTab('table');
	        fetchTableData(1, pageLimit, searchQuery, sortConfig);

	        // Remove red border styling
	        document.querySelectorAll('.ui-helper-redBoarder').forEach(el => {
	          el.classList.remove('ui-helper-redBoarder');
	        });
	      } else if (data.failureMessage) {
	        PopupService.getInstance().showMessage(data.failureMessage, false);
	      }
	    } catch (error) {
	      console.error('Error submitting matching criteria:', error);
	      PopupService.getInstance().showMessage('Error occurred while submitting matching criteria', false);
	    } finally {
	      setIsApiLoading(false);
	    }
	  } else {
	    PopupService.getInstance().showMessage(errorMessage, false);
	  }
	};


	async function handleAlertComparison(selectedMrNumber, mongoId) {
		setLoading(true);
		await callApi(async () => {
			setIsApiLoading(true);
			try {
				const response = await apiFetch(`${config.API_BASE_URL}/compareAlert`, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						'X-Username': username,
						'Authorization': `Bearer ${mrToken}`
					},
					body: JSON.stringify({
						batchMatchId: mongoId,
						mrNumber: selectedMrNumber,
						toBeComparedMrNumber: comparedMrNumber
					})
				});
				if (!response.ok) throw new Error('Failed to compare alerts');
				const result = await response.json();
				setComparisonResult(result.data);
				setTimeout(() => {
					const el = document.getElementById('duplicate_loader');
					if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
				}, 200);
			} catch (error) {
				console.error(error);
			} finally {
				setLoading(false);
				setIsApiLoading(false);
			}
		});
	}


	const addAliases = async () => {
		try {
			// Get the current product criteria from the input field
			const productCriteria = document.getElementById('product_criteria').value.trim();

			if (!productCriteria) {
				PopupService.getInstance().showMessage('Please enter product criteria first', false);
				return;
			}

			// Show loading state
			setIsApiLoading(true);

			// Add a random parameter to prevent caching
			const randomParam = Math.random();

			// Make API call to get aliases
			const response = await apiFetch(`${config.API_BASE_URL}/addProductAlias?rand=${randomParam}`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json', 'X-Username': username, 'Authorization': `Bearer ${mrToken}` },
				body: JSON.stringify({
					productCriteria: productCriteria
				})
			});

			const data = await response.json();

			if (data.successMessage || (data.message && data.data && data.data.ALIAS_PRODUCT_CRITERIA)) {
				// Get the new aliases
				const newAliases = data.data.ALIAS_PRODUCT_CRITERIA;

				// Create an array of current criteria terms
				const currentTerms = productCriteria.split(/\s+/).filter(term => term.trim());

				// Create an array of new alias terms
				const newAliasTerms = newAliases.split(/\s+/).filter(term => term.trim());

				// Filter out aliases that already exist in the current criteria
				const uniqueNewAliases = newAliasTerms.filter(term => !currentTerms.includes(term));

				if (uniqueNewAliases.length > 0) {
					// Combine original criteria with unique new aliases
					const updatedCriteria = productCriteria + " " + uniqueNewAliases.join(" ");

					// Update the product criteria field
					document.getElementById('product_criteria').value = updatedCriteria;

					// Show success message
					PopupService.getInstance().showMessage('Product aliases added successfully', true);
				} else {
					PopupService.getInstance().showMessage('No new product aliases found', false);
				}
			} else if (data.failureMessage) {
				PopupService.getInstance().showMessage(data.failureMessage, false);
			} else {
				PopupService.getInstance().showMessage('No product aliases found', false);
			}
		} catch (error) {
			console.error('Error adding product aliases:', error);
			PopupService.getInstance().showMessage('Error occurred while adding product aliases', false);
		} finally {
			setIsApiLoading(false);
		}
	};


	// Place this inside your Home component, before the return statement


	const renderStatusPopup = () => {
		if (!showStatusPopup || !statusRow) return null;
		return (
			<div
				className="modal-backdrop"
				style={{
					zIndex: 1050,
					position: 'fixed',
					top: 0,
					left: 0,
					width: '100vw',
					height: '100vh',
					background: 'rgba(0,0,0,0.3)'
				}}
			>
				<div
					className="modal d-block"
					tabIndex="-1"
					style={{
						zIndex: 1060,
						position: 'fixed',
						top: '50%',
						left: '50%',
						transform: 'translate(-50%, -50%)',
						width: 280,
						height: 200, // 20% of previous ~350px height
						background: '#fff',
						borderRadius: 8,
						padding: 16,
						boxShadow: '0 2px 16px rgba(0,0,0,0.15)',
						display: 'flex',
						flexDirection: 'column',
						justifyContent: 'center'
					}}
				>
					<div style={{ fontSize: 15, fontWeight: 500, marginBottom: 8 }}>
						Change status for {statusRow.mrNumber} - {statusRow.clientId}
					</div>
					<select
						className="form-select my-2"
						value={selectedStatus}
						onChange={e => setSelectedStatus(e.target.value)}
						style={{ minWidth: 180 }}
					>
						{STATUS_OPTIONS.map(opt => (
							<option key={opt} value={opt}>{opt}</option>
						))}
					</select>
					<div className="d-flex justify-content-end gap-2 mt-2">
						<button
							className="btn btn-sm btn-danger"
							disabled={statusLoading}


							// In the Update button's onClick handler in renderStatusPopup
							onClick={async () => {
								setStatusLoading(true);
								try {
									const resp = await apiFetch(`${config.API_BASE_URL}/updateBatchXLStatus`, {
										method: 'POST',
										headers: {
											'Content-Type': 'application/json',
											'X-Username': username,
											'Authorization': `Bearer ${mrToken}`
										},
										body: JSON.stringify({
											mongoId: statusRow.mongoId,
											status: selectedStatus
										})
									});

									if (resp.ok) {
										PopupService.getInstance().showMessage('Status updated successfully', true);
										setShowStatusPopup(false);

										// Find and update the correct detail row
										setRowDetails(prev => {
											let updated = { ...prev };
											Object.keys(updated).forEach(parentId => {
												updated[parentId] = updated[parentId].map(detail =>
													detail.mongoId === statusRow.mongoId
														? { ...detail, status: selectedStatus }
														: detail
												);
											});
											return updated;
										});

									} else {
										PopupService.getInstance().showMessage('Failed to update status', false);
									}
								} catch {
									PopupService.getInstance().showMessage('Error updating status', false);
								} finally {
									setStatusLoading(false);
								}
							}}


						>
							{statusLoading ? 'Updating...' : 'Update'}
						</button>
						<button
							className="btn btn-secondary"
							disabled={statusLoading}
							onClick={() => setShowStatusPopup(false)}
						>
							Cancel
						</button>
					</div>
				</div>
			</div>
		);
	};



	const handleValidateRouting = async () => {
		setLoading(true);
		await callApi(async () => {
			setIsApiLoading(true);
			try {
				const response = await apiFetch(`${config.API_BASE_URL}/validateRouting`, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						'X-Username': username,
						'Authorization': `Bearer ${mrToken}`
					},
					body: JSON.stringify({ routingString: mappedRouting, clientId: selectedClientId })
				});
				if (response.ok) {
					const data = await response.json();
					const validList = data?.data?.VALID_ROUTING || [];
					const inValidList = data?.data?.INVALID_ROUTING || [];

					// Split mappedRouting by comma, trim, and filter out empty lines
					const lines = mappedRouting.split(',').map(line => line.trim()).filter(Boolean);

					// Highlight each line
					const highlighted = lines.map(line => {
						if (validList.includes(line)) {
							return `<span class="bg-info">${line} </span>&nbsp;<i class="fa fa-check text-success "></i>`;
						} else if (inValidList.includes(line)) {
							return `<span class="bg-danger text-white">${line}</span>&nbsp; <i class="fa fa-times text-danger"></i>`;
						} else {
							return line;
						}
					}).join('<br/>');

					setHighlightedMappedRouting(highlighted);

					setTimeout(() => {
						const el = document.getElementById('highlight_routing');
						if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
					}, 100);

					PopupService.getInstance().showMessage('Please check the validation result', true);
				}
			} catch (e) {
				setHighlightedMappedRouting('');
				PopupService.getInstance().showMessage('Failed to validate routing', false);
			} finally {
				setLoading(false);
				setIsApiLoading(false);
			}
		});
	};


	const handleMarkForDistribution = async (row) => {
		setLoading(true);
		await callApi(async () => {
			setIsApiLoading(true);
			try {
				const response = await apiFetch(`${config.API_BASE_URL}/schedule-distribution`, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						'X-Username': username,
						'Authorization': `Bearer ${mrToken}`
					},
					body: JSON.stringify({
						routingString: mappedRouting,
						mongoId: row.mongoId,
						username: username
					}),
				});
				const data = await response.json();
				if (!response.ok) {
					PopupService.getInstance().showMessage('Alert Marked for Distribution Not Successfull: ' + data.error, false);
					return;
				}
				if (response.ok) {
					// Inside handleMarkForDistribution, after a successful response
					const invalidPairs = data?.data?.INVALID_ROUTING_PAIRS || [];
					const distributionStatus = data?.data?.DISTRIBUTION_STATUS || 'MARKED_DISTRIBUTION';


					const highlightDiv = document.getElementById('highlight_routing2');
					highlightDiv.innerHTML = '';
					if (Array.isArray(invalidPairs) && invalidPairs.length > 0) {
						if (highlightDiv) {
							// Clear previous content
							const html = `<div class="mt-2"><strong>Invalid Routing Pairs:</strong><br>${invalidPairs.map(pair => `<span class="text-danger">${pair}</span>`).join('<br>')}</div>`;
							highlightDiv.innerHTML += html;
						}
					}


					// Update status of the expanded row detail
					setRowDetails(prev => {
						const updated = { ...prev };
						Object.keys(updated).forEach(parentId => {
							updated[parentId] = updated[parentId].map(detail =>

								detail.mongoId === row.mongoId
									? (distributionStatus === 'REDISTRIBUTE'
										? detail
										: { ...detail, status: distributionStatus })
									: detail

							);
						});
						return updated;
					});

					PopupService.getInstance().showMessage(data?.data?.message, true);
				}



			} catch (error) {
				console.error(error);
			} finally {
				setLoading(false);
				setIsApiLoading(false);
			}
		});
	};


	return (
		<>
			<Header />
			<div className="container-fluid text-secondary">
				<br /><br /><br />
				<div className="card text-secondary">
					<div className="card-header">Home</div>
					<div className='card-body  custom-card-bg'>

						<div className='card' id="mark_distribute" style={{ display: showMarkDistribute ? 'block' : 'none' }}>
							<div className="card-header text-secondary">
								<i className="fa fa-sitemap"></i> <strong>Mark For Distribution</strong>
								{markDistRow && (
									<span style={{ marginLeft: 16 }}>
										<span className="fw-bold">MR Number:</span> {markDistRow.mrNumber} &nbsp;|&nbsp;{markDistRow.alertTitle}&nbsp;
										<span className="fw-bold">Client ID:</span> {markDistRow.clientId} - {markDistRow.clientName}
									</span>
								)}
								<button
									className="btn btn-sm btn-outline-secondary float-end"
									onClick={() => setShowMarkDistribute(false)}
									style={{ marginLeft: 8 }}
								>Close</button>
							</div>
							<div className='card-body'>
								<div className="row">
									<div className="col-md-6">
										<label className="fw-bold text-success">Mapped Routing</label>

										<textarea
											className="form-control"
											rows={10}
											value={mappedRouting}
											name="mapped_routing"
											onChange={e => setMappedRouting(e.target.value)}
										/>

										{highlightedMappedRouting && (
											<div id="highlight_routing"
												className="mt-2"
												style={{ whiteSpace: 'pre-wrap', background: '#f8f9fa', borderRadius: 4, padding: 8 }}
												dangerouslySetInnerHTML={{ __html: highlightedMappedRouting }}
											/>

										)}
										<div id="highlight_routing2">
										</div>
									</div>
									<div className="col-md-6">
										<label className="fw-bold text-danger">Unmapped Routing</label>
										<textarea
											className="form-control"
											rows={10}
											value={unmappedRouting}
											readOnly
										/>
									</div>
								</div>
								<div className="mt-3 d-flex justify-content-center gap-3">
									<button
										type="button"
										id="add_attachments"
										className="btn btn-warning btn-sm"
										onClick={handleValidateRouting}
									>
										Validate Routing
									</button>


									<button
										type="button"
										id="create_alert"
										className="btn btn-danger btn-sm"
										onClick={() => markDistRow && handleMarkForDistribution(markDistRow)}
									>
										Mark for Distribution
									</button>


								</div>
							</div>
						</div>

						<div className="card" id="main_card">
							<div className="card-header text-secondary"><i className="fa fa-home"></i>  <strong>Home</strong></div>
							<div className="card-body  text-secondary">
								<ul className="nav nav-tabs " role="tablist">
									<li className="nav-item" role="presentation">
										<button
											className={`nav-link ${activeTab === 'table' ? 'active' : 'text-secondary'}`}
											onClick={() => {
												setActiveTab('table');
												setBatchMatchCriteria(false);
												setShowAlertDetailTab(false);
												setDuplicates([]);
												setSupportAlert(null);
												setShowDuplicatesTab(false);
												setComparisonResult(null);
											}}
											type="button"
											role="tab"
										>
											Configured Matches
										</button>
									</li>
									<li className="nav-item" role="presentation">
										<button
											className={`nav-link ${activeTab === 'second' ? 'active' : 'text-secondary'}`}
											onClick={() => {
												setActiveTab('second');
												setBatchMatchCriteria(false);
												setDuplicates([]);
												setSupportAlert(null);
												setShowDuplicatesTab(false);
												setComparisonResult(null);
											}}
											type="button"
											role="tab"
										>
											Matching Criteria Entry
										</button>
									</li>
									<li className="nav-item" role="presentation" style={{ display: showDuplicatesTab ? 'block' : 'none' }}>
										<button
											className={`nav-link ${activeTab === 'third' ? 'active' : 'text-secondary'}`}
											onClick={() => {
												setActiveTab('third');
												setBatchMatchCriteria(false);
												setDuplicates([]);
												setSupportAlert(null);
											}}
											type="button"
											role="tab"
										>
											Possible Duplicates
										</button>
									</li>
									{activeTab === 'third' && (

										<div id="duplicate_loader" className="card border-0">
											<hr />
											<div className="card-body">

												{duplicates.length > 0 && supportAlert && (
													<div className="row">
														{/* Left: SUPPORT_ALERT */}
														<div className="form-group col-md-6" id="main_div">
															<label className="fw-bold mb-2">MR Number</label>
															<ul className="list-group">
																<li className="list-group-item">
																	<span className="badge bg-info">{supportAlert.mrNumber}</span>
																	-{supportAlert.alertSource}
																	-{supportAlert.alertClass}
																	-{supportAlert.publishDateStr}
																	<br />
																	{supportAlert.alertTitle}
																</li>
															</ul>
														</div>
														{/* Right: DUPLICATE_ALERT_LIST with radio buttons */}
														<div className="form-group col-md-6" id="compare_div">
															<label className="fw-bold mb-2">To Be Compared With</label>
															<ul className="list-group">
																{duplicates.map((item, idx) => (
																	<li className="list-group-item" key={item.mrNumber}>

																		<input
																			type="radio"
																			name="mrNumber"
																			value={item.mrNumber}
																			//checked={String(selectedMrNumber) === String(item.mrNumber)}
																			onChange={() => setSelectedMrNumber(String(item.mrNumber))}
																		/>

																		&nbsp;
																		<span className="badge bg-primary">{item.mrNumber}</span>
																		-{item.alertSource}
																		-{item.alertClass}
																		- <b>Publish Date:</b>&nbsp;{item.publishDateStr}
																		<br />
																		<span>{item.alertTitle}</span>
																	</li>
																))}
															</ul>
															<button
																className="btn btn-primary btn-sm mt-3"
																onClick={() => handleAlertComparison(selectedMrNumber, selectedMongoId)}

															>
																Compare Alert
															</button>

														</div>
													</div>


												)}
												{(!duplicates.length || !supportAlert) && (
													<div className="alert alert-info">No possible duplicates found.</div>
												)}
											</div>

											{comparisonResult && (
												<div className='card border-0'>

													<div className='card-body'>
														<div className="row">
															<div className="col">
																<div className='card'>
																	<div className='card-header'>MR Number:{comparedMrNumber}</div>
																	<div className='card-body'>

																		<div
																			dangerouslySetInnerHTML={{
																				__html: renderDataPairsHtml(comparisonResult.ALERT)
																			}}
																		/>
																	</div>
																</div>
															</div>
															<div className="col">
																<div className='card'>
																	<div className='card-header'>To Be Compared Mr Number:{selectedMrNumber}</div>
																	<div className='card-body'>
																		<div
																			dangerouslySetInnerHTML={{
																				__html: renderDataPairsHtml(comparisonResult.TO_BE_COMPARED_ALERT)
																			}}
																		/>
																	</div>
																</div>
															</div>
														</div>
													</div>
												</div>
											)}
										</div>

									)}


									{showAlertDetailTab && (
										<li className="nav-item" role="presentation">
											<button
												className={`nav-link ${activeTab === 'alertDetail' ? 'active' : 'text-secondary'}`}
												onClick={() => setActiveTab('alertDetail')}
												type="button"
												role="tab"
											>
												Alert Detail
											</button>
										</li>
									)}
								</ul>

								<div className="tab-content p-3 border-0 ">
									{activeTab === 'table' && (
										<div className="tab-pane fade show active ">
											<div className="d-flex flex-wrap justify-content-between align-items-center mb-2">
												<span className='text-primary small fst-italic'>---&nbsp;Has Possible Duplicates (Select from the dropdown actions to view them).</span>
												

												<div
												  className="ms-auto d-flex align-items-center gap-2"
												  style={{ flexWrap: 'nowrap', alignItems: 'center' }}
												>
												  <select
												    id="pageSizeDropdown"
												    className="form-select"
												    value={pageLimit}
												    onChange={e => { setPageLimit(Number(e.target.value)); setCurrentPage(1); }}
												    style={{ width: '100px' }}
												  >
												    <option value={20}>20</option>
												    <option value={50}>50</option>
												    <option value={100}>100</option>
												  </select>
												  <SearchComponent
												    ref={searchComponentRef}
												    viewMode={viewMode}
												    toggleViewMode={() => setViewMode(viewMode === 'table' ? 'card' : 'table')}
												    style={{ width: '450px' }}
													showToggleViewButton={false}
													showRefreshButton={true}
												    onSearch={searchText => {
												      setSearchQuery(searchText);
												      setCurrentPage(1);
												      fetchTableData(1, pageLimit, searchText, sortConfig);
												    }}
												  />
												</div>

												
											</div>
											{showTable && (
											  loading ? (
											    <div>Loading...</div>
											  ) : (
											    <>
											      {/* Top horizontal scrollbar */}
											      <div
											        ref={topScrollRef}
											        style={{
											          overflowX: 'auto',
											          overflowY: 'hidden',
											          marginBottom: '2px',
											          display: tableScrollWidth > 0 ? 'block' : 'none'
											        }}
											      >
											        <div style={{ width: `${tableScrollWidth}px`, height: '1px' }} />
											      </div>
													
														<div className="table-responsive" ref={tableScrollRef}>
															<table className={`table table-responsive-sm  text-secondary table-bordered table-striped  ${showTable ? 'card-view' : ''}`}>
																<thead>
																	<tr >
																		<th></th>
																		<th className="pointer-cursor" onClick={() => handleSort('index')}>
																			#
																		</th>
																		<th>Action</th>
																		<th className="text-nowrap pointer-cursor" onClick={() => handleSort('mrNumber')}>
																			MR Number
																			{renderSortIcon('mrNumber')}
																		</th>
																		<th className="pointer-cursor" onClick={() => handleSort('matchStatus')}>
																			Status
																			{renderSortIcon('matchStatus')}
																		</th>
																		<th className="fixed-width-alert-title pointer-cursor" onClick={() => handleSort('alertTitle')}>
																			Alert Title
																			{renderSortIcon('alertTitle')}
																		</th>
																		<th className="text-nowrap pointer-cursor" onClick={() => handleSort('source')}>
																			MR Source
																			{renderSortIcon('source')}
																		</th>
																		<th className="text-nowrap pointer-cursor" onClick={() => handleSort('matchesFound')}>
																			Matches Found
																			{renderSortIcon('matchesFound')}
																		</th>
																		<th className="text-nowrap pointer-cursor" onClick={() => handleSort('createTimestamp')}>
																			Match Submitted
																			{renderSortIcon('createTimestamp')}
																		</th>
																		<th className="text-nowrap pointer-cursor" onClick={() => handleSort('processedTimestamp')}>
																			Match Processed
																			{renderSortIcon('processedTimestamp')}
																		</th>
																		<th className="text-nowrap pointer-cursor" onClick={() => handleSort('submittedBy')}>
																			Submitted By
																			{renderSortIcon('submittedBy')}
																		</th>
																	</tr>
																</thead>
																<tbody>
																	{tableData.length === 0 ? (
																		<tr>
																			<td colSpan={11} className="text-center">No data found</td>
																		</tr>
																	) : (
																		tableData.map((row, idx) => (
																			<React.Fragment key={row.mongoId}>
																				<tr id={`row_${row.mongoId}`}>
																					<td>
																						<button
																							className="btn btn-link text-info"
																							onClick={() => handleExpandRow(row.mongoId)}
																							aria-label={expandedRows[row.mongoId] ? "Collapse" : "Expand"}
																						>
																							{expandedRows[row.mongoId]
																								? <FontAwesomeIcon icon={faMinus} className="thick-icon" title="Click to collapse" />
																								: <FontAwesomeIcon icon={faPlus} className="thick-icon" title="Click to expand and view available options." />}
																						</button>
																					</td>
																					<td>{(currentPage - 1) * pageLimit + idx + 1}</td>

																					<td style={{ minWidth: '150px' }}>
																						<select
																							className="form-select form-select-sm"
																							defaultValue=""
																							onChange={e => {
																								const action = e.target.value;
																								if (action === "summary") handleMrNumberClick(row.mongoId, row.mrNumber, row.alertTitle);
																								else if (action === "criteria") loadMatchCriteria(String(row.mongoId));
																								else if (action === "duplicates") loadDuplicates(String(row.mongoId), String(row.mrNumber));
																								e.target.value = ""; // Reset dropdown after action
																							}}
																						>
																							<option value="" disabled>Select Action</option>
																							{row.matchStatus !== 'BATCH_SUBMITTED' && (
																							  <option value="summary">Match Summary</option>
																							)}
																							<option value="criteria">Match Criteria</option>
																							{row.duplicateCount > 0 && (
																								<option value="duplicates">Duplicates({row.duplicateCount})</option>
																							)}
																						</select>
																					</td>

																					<td className="text-nowrap ">

																					{row.matchStatus == 'BATCH_SUBMITTED' ? (

																						<button
																						  className="btn btn-link text-secondary"
																						  title="Click to view Matching Summary"
																						  onClick={() => PopupService.getInstance().showMessage('Matching is currently unavailable. Please try again shortly.', false)}
																						>
																						  {row.mrNumber}
																						</button>

																					) : (
																					  <button
																					    className="btn btn-link text-secondary"
																					    title="Click to view Matching Summary"
																					    onClick={() => handleMrNumberClick(row.mongoId, row.mrNumber, row.alertTitle)}
																					  >
																					    {row.mrNumber}
																					  </button>
																					)}

																					</td>
																					<td className={
																						row.matchStatus === 'BATCH_COMPLETED' ? 'text-info' :
																							row.matchStatus === 'FINAL_REVIEW_DONE_ALL' ? 'text-success' :
																								row.matchStatus === 'FINAL_REVIEW_DONE_FEW' ? 'text-warning' :
																									row.matchStatus === 'DRAFT_REVIEW_DONE_FEW' ? 'text-primary' :
																										'text-danger'
																					}>
																						{row.matchStatus}
																					</td>
																					<td
																						className={`fixed-width-alert-title${row.duplicateCount > 0 ? ' text-primary' : ''}`}
																						title={row.duplicateCount > 0 ? `Has possible duplicates - ${row.duplicateCount}` : undefined}
																					>
																						{row.alertTitle && row.alertTitle.length > 70 ? `${row.alertTitle.slice(0, 70)}.....` : row.alertTitle}
																					</td>
																					<td >{row.source}</td>
																					<td >{row.matchesFound}</td>
																					<td className="text-nowrap ">{row.createTimestamp ? format(new Date(row.createTimestamp), 'MM/dd/yyyy hh:mm a') : ''}</td>
																					<td className="text-nowrap ">{row.processedTimestamp ? format(new Date(row.processedTimestamp), 'MM/dd/yyyy hh:mm a') : ''}</td>
																					<td >{row.submittedBy}</td>
																				</tr>
																				{expandedRows[row.mongoId] && (
																					<tr>
																						<td colSpan={12}>

																							<table className="table table-sm  table-bordered table-striped  mb-0 ">
																								<thead className="nested-table-header">
																									<tr>
																										<th>#</th>
																										<th>Action</th>
																										<th style={{ width: '270px' }}>Client ID</th>
																										<th>MR Number</th>
																										<th style={{ width: '120px' }}>Match Count</th>
																										<th>Status</th>
																										<th>Final Review</th>
																										<th>Review Time</th>
																									</tr>
																								</thead>
																								<tbody>
																									{(rowDetails[row.mongoId] || []).map((detail, i) => (

																										<tr className={
																											detail.status === 'EXPORTED' ? 'table-info' :
																												detail.status === 'PRODUCT_MATCH_REPLACED' ? 'table-primary ' :
																													detail.status === 'MARKED_DISTRIBUTION' ? 'table-warning text-white' :
																														detail.status === 'DISTRIBUTED' ? 'table-success text-white' :
																															detail.status === 'PENDING' ? 'table-danger text-white' :
																																detail.status === 'PROCESSED' ? 'table-secondary' :
																																	'table-secondary'
																										}
																											key={i} >
																											<td>{i + 1}</td>
																											<td id="nest_table_action">
																												<select
																													className="form-select"
																													onChange={(e) => handleDropdownChange(e.target.value, detail)}
																													value={dropdownValue}
																												>
																													<option value="" >
																														Select Action
																													</option>
																													<option value="Download Product Match">Download Product Match</option>
																													<option value="Download Routing Sheet">Download Routing Sheet</option>
																													<option value="Export To Tracking">Export To Tracking</option>
																													<option value="Mark for Distribution">Mark for Distribution</option>
																													<option value="Change Status">Change Status</option>
																													{detail.routingType === 'wc' && (
																														<>
																															<option value="Wildcard Product Match">Wildcard Product Match</option>
																															<option value="Wildcard Routing Sheet">Wildcard Routing Sheet</option>
																														</>
																													)}
																												</select>
																											</td>
																											
																											<td>{detail.clientId}-{detail.clientName}</td>
																											
																											<td>{detail.mrNumber}</td>
																											<td>{detail.matchCount}</td>
																											<td className={
																												detail.status === 'EXPORTED' ? 'text-info' :
																													detail.status === 'PRODUCT_MATCH_REPLACED' ? 'text-dark' :
																														detail.status === 'MARKED_DISTRIBUTION' ? 'text-dark' :
																															detail.status === 'DISTRIBUTED' ? 'text-success' :
																																detail.status === 'PENDING' ? 'text-danger' :
																																	detail.status === 'PROCESSED' ? 'bg-secondary' :
																																		'text-muted'
																											}>
																												{detail.status}
																												{detail.status === 'PENDING' && (
																													<>
																														<br />
																														<cite className="text-secondary" title="Has unmapped routing" style={{ marginLeft: 6 }}>- has unmapped routing</cite>
																													</>
																												)}
																											</td>
																											<td>{detail.finalReviewBy}</td>
																											<td>{detail.finalReviewTimestamp}</td>
																										</tr>
																									))}
																									{(rowDetails[row.mongoId] || []).length === 0 && (
																										<tr>
																											<td colSpan={4}>No details found.</td>
																										</tr>
																									)}
																								</tbody>
																							</table>
																						</td>
																					</tr>
																				)}
																			</React.Fragment>
																		))
																	)}
																</tbody>
															</table>
														</div>

														<div className="pagination-container">

															<span className="text-secondary">Page {currentPage} of Total Pages: {totalPages} - Total {totalRecords}</span>
															<nav>
																<ul className="pagination mb-0 flex-nowrap">
																	<li className={`page-item${currentPage === 1 ? ' disabled' : ''}`}>
																		<button className="page-link text-secondary" onClick={() => { setCurrentPage(1); setGotoPageInput(''); }} disabled={currentPage === 1}>
																			<FontAwesomeIcon icon={faAngleDoubleLeft} />
																		</button>
																	</li>
																	<li className={`page-item${currentPage === 1 ? ' disabled' : ''}`}>
																		<button className="page-link text-secondary" onClick={() => { setCurrentPage(currentPage - 1); setGotoPageInput(''); }} disabled={currentPage === 1}>
																			Previous
																		</button>
																	</li>
																	{pageNumbers.map((page) => (
																		<li key={page} className={`page-item${currentPage === page ? ' active' : ''}`}>
																			<button
																				className={`page-link ${currentPage === page ? 'text-light' : 'text-secondary'}`}
																				onClick={() => { setCurrentPage(page); setGotoPageInput(''); }}
																			>
																				{page}
																			</button>
																		</li>
																	))}
																	<li className={`page-item${currentPage === totalPages ? ' disabled' : ''}`}>
																		<button className="page-link text-secondary" onClick={() => { setCurrentPage(currentPage + 1); setGotoPageInput(''); }} disabled={currentPage === totalPages}>
																			Next
																		</button>
																	</li>
																	<li className={`page-item${currentPage === totalPages ? ' disabled' : ''}`}>
																		<button className="page-link text-secondary rounded-end" onClick={() => { setCurrentPage(totalPages); setGotoPageInput(''); }} disabled={currentPage === totalPages}>
																			<FontAwesomeIcon icon={faAngleDoubleRight} />
																		</button>
																	</li>
																	&nbsp;&nbsp;
																	<li>
																		<div className="d-flex align-items-center">
																			<input
																				type="number"
																				min="1"
																				max={totalPages}
																				value={gotoPageInput}
																				onChange={e => setGotoPageInput(e.target.value)}
																				onKeyDown={e => {
																					if (e.key === 'Enter') {
																						handleGotoPage();
																					}
																				}}
																				className="form-control"
																				style={{ width: 100, marginRight: 4 }}
																				placeholder="Page #"
																				aria-label="Go to page"
																			/>
																			<button
																				className="btn btn-secondary btn-sm"
																				onClick={handleGotoPage}
																				type="button"
																				style={{ marginRight: 8 }}
																			>
																				Go
																			</button>
																		</div>
																	</li>
																</ul>
															</nav>
														</div>

													</>
												)
											)}
										</div>
									)}
									{activeTab === 'second' && (
										<div className="tab-pane fade show active">
											{/* Duplicates UI with SUPPORT_ALERT */}
											{duplicates.length > 0 && supportAlert && (
												<div className="row">
													{/* Left: SUPPORT_ALERT */}
													<div className="form-group col-md-6" id="main_div">
														<ul className="list-group">
															<li className="list-group-item">
																<span className="badge bg-info">{supportAlert.mrNumber}</span>
																-{supportAlert.alertSource}
																-{supportAlert.alertClass}
																-{supportAlert.publishDateStr}
																<br />
																{supportAlert.alertTitle}
															</li>
														</ul>
													</div>
													{/* Right: DUPLICATE_ALERT_LIST */}
													<div className="form-group col-md-6" id="compare_div">
														<ul className="list-group">
															{duplicates.map((item, idx) => (
																<li className="list-group-item" key={item.mrNumber}>
																	<input
																		type="radio"
																		name="sel_mr"
																		value={item.mrNumber}
																		aria-label="Radio button for following text input"
																	/>&nbsp;
																	<span className="badge bg-primary">{item.mrNumber}</span>
																	-{item.alertSource}
																	-{item.alertClass}
																	- <b>Publish Date:</b>&nbsp;{item.publishDateStr}
																	<br />
																	{item.alertTitle}
																</li>
															))}
														</ul>
														<button
															className="btn btn-primary btn-sm mt-3"
															onClick={() => {
																setDuplicates([]);
																setSupportAlert(null);
															}}
														>
															Back to Matching Criteria Entry
														</button>
													</div>
												</div>
											)}

											{/* Matching Criteria Entry Form */}
											{!batchMatchCriteria && duplicates.length === 0 && (
												<div id="matching_div">
													{/* ...your form code here (unchanged)... */}
													<div className="card">
														<div className="card-header">Configure Matching Criteria</div>
														<div className="card-body">


															<form id="match_criteria_form" autoComplete="off">
																<div className="form-row d-flex gap-5" style={{ position: 'relative' }}>
																	<div className="form-group col-md-2" id="mr_number_div">
																		<label htmlFor="mr_number" className="text-info fw-bold" id="mrn_label">MR Number</label>
																		<input
																			type="text"
																			className="form-control basicAutoComplete"
																			id="mr_number"
																			data-noresults-text="No MR Number Found"
																			autoComplete="off"
																			placeholder="Enter MR Number here"
																			value={mrNumber}
																			onChange={handleMrNumberChange}
																		/>
																		{showSuggestions && suggestions.length > 0 && (
																			<ul className="list-group position-absolute w-100" style={{ zIndex: 10 }}>
																				{suggestions.map((item, idx) => (
																					<li
																						key={idx}
																						className="list-group-item list-group-item-action"
																						style={{ cursor: 'pointer' }}
																						onMouseDown={() => handleSuggestionClick(item)}
																					>
																						{item.mrNumber}
																					</li>
																				))}
																			</ul>
																		)}
																	</div>
																	<div
																		className="form-group col-md-9"
																		id="mr_number_title_div"
																		style={{ display: showMrNumberTitle ? 'block' : 'none' }}
																	>
																		<label className="text-info" id="mrn_label">Alert Title</label>
																		<div className="alert alert-info" id="mr_number_title" role="alert">
																			{mrNumberTitle}
																		</div>
																	</div>
																</div>
																<div>
																	<div className="form-row d-flex gap-5">
																		<div className="form-group col-md-6" id="product_criteria_div">
																			<label htmlFor="product_criteria" className="text-info fw-bold">Product Criteria</label>

																			<textarea
																				className="form-control"
																				id="product_criteria"
																				rows="3"
																				placeholder="Enter Product Criteria Here"
																			></textarea>
																			<span
																				className="badge bg-success"
																				title="Click to Add Aliases"
																				style={{ cursor: 'pointer' }}
																				onClick={addAliases}
																			>
																				Add Aliases
																			</span>

																		</div>
																		<div className="form-group col-md-5" id="manufacturer_criteria_div">
																			<label htmlFor="manufacturer_criteria" className="text-info fw-bold">Manufacturer Criteria</label>
																			<textarea
																				className="form-control"
																				id="manufacturer_criteria"
																				rows="3"
																				placeholder="Enter Manufacturer Criteria Here"
																			></textarea>
																		</div>
																	</div>
																	<div className="form-row d-flex gap-5">
																		<div className="form-group col-md-6" id="ctlg_div">


																			<div className="form-group">
																				<br />
																				<label htmlFor="prd-code-operator" className="text-info fw-bold">Catalog Id Type</label>
																				<select className="form-control" name="prdcodeoperator" id="prd-code-operator">
																					<option value="">Select Catalog Id Type</option>
																					<option value="catalog_no">Catalog No</option>
																					<option value="equipment_id_no">Equipment Id No</option>
																					<option value="GTIN">GTIN</option>
																					<option value="kit_no">Kit Number</option>
																					<option value="item_no">Item No</option>
																					<option value="lot_no">Lot No</option>
																					<option value="mac_id">MAC ID</option>
																					<option value="mfr_item_id">Manufacturer Item ID</option>
																					<option value="mfr_product_id">Manufacturer Product ID</option>
																					<option value="model_no">Model No</option>
																					<option value="ndc_no">NDC No</option>
																					<option value="part_no">Part No</option>
																					<option value="product_code">Product Code</option>
																					<option value="product_id_no">Product Id No</option>
																					<option value="reference_no">Reference No</option>
																					<option value="serial_no">Serial No</option>
																					<option value="sku_code">SKU Code</option>
																					<option value="upn_code">UPN Code</option>
																					<option value="upc_code">UPC Code</option>
																					<option value="vendor_prd_id">Vendor Product Id</option>
																					<option value="vendor_item_id">Vendor Item Id</option>
																					<option value="catalog_range">Catalog Range</option>
																				</select>
																				<label htmlFor="catalog_criteria" className="text-info fw-bold">Catalog Criteria</label>
																				<textarea
																					className="form-control "
																					id="catalog_criteria"
																					rows="5"
																					placeholder="Enter Catalog Criteria Here"
																				></textarea>
																				<br />
																				<button
																					type="button"
																					id="add_ctlg_croteria_btn"
																					className="btn btn-success btn-sm"
																					onClick={addCtlgCriteria}
																				>
																					Add To Catalog Criteria
																				</button>
																				&nbsp;
																				<button
																					type="button"
																					id="add_clr_ctlg_crt_btn"
																					className="btn btn-primary btn-sm"
																					onClick={clearCtlgCriteria}
																				>
																					Clear Catalog Criteria
																				</button>

																			</div>
																		</div>
																		<div className="form-group col-md-5" id="final_ctlg_div">
																			<br />
																			<label htmlFor="catalog_criteria" className="text-info fw-bold">Final Catalog Criteria</label>
																			<textarea
																				className="form-control"
																				id="final_catalog_criteria"
																				rows="8"
																				placeholder="Final Catalog Criteria"
																			></textarea>
																			<br />

																		</div>
																	</div>
																	<div className="form-row d-flex justify-content-center mt-3">

																		<button
																			type="button"
																			id="add_match"
																			className="btn btn-danger btn-sm"
																			onClick={addNewMatchingCriteria}
																		>
																			Save Match Configuration
																		</button>

																	</div>
																</div>
															</form>
														</div>
													</div>
												</div>
											)}

											{/* Matching Criteria Display */}
											{batchMatchCriteria && (
												<div id="matching_crt_div" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column' }}>
													<div className="d-flex gap-3">
														<button
															className="btn btn-success btn-sm mt-3 mb-3"
															onClick={() => {
																setBatchMatchCriteria(false);
																setShowAlertDetailTab(false);
																setActiveTab('second');
																setMrNumber('');
																setMrNumberTitle('');
																setShowMrNumberTitle(false);
															}}
														>
															<FontAwesomeIcon icon={faAngleDoubleLeft} className="mr-2" />Back to Matching Criteria
														</button>
														<button
															className="btn btn-primary btn-sm mt-3 mb-3"
															onClick={() => {
																setBatchMatchCriteria(false);
																setActiveTab('table');
																setShowAlertDetailTab(false);
															}}
														>
															<FontAwesomeIcon icon={faAngleDoubleLeft} className="mr-2" />Back to Matching Summary
														</button>
													</div>
													<div className="form-row d-flex flex-wrap">
														<div className="form-group col-md-4" id="product_criteria_div">
															<div className="card">
																<div className="card-header">Product & Manufacturer Criteria - {batchMatchCriteria.mrNumber}</div>
																<div className="card-body col-md-12">
																	<div className="alert alert-info" role="alert">
																		<b>Product Criteria:</b> {batchMatchCriteria.productCriteria}
																	</div>
																	<div className="alert alert-primary" role="alert">
																		<b>Manufacturer Criteria:</b> {batchMatchCriteria.mfrCriteria}
																	</div>
																</div>
															</div>
														</div>
														<div className="form-group col-md-1" id="blank_div"></div>
														<div className="form-group col-md-7" id="ctlg_criteria_div">
															<div className="card">
																<div className="card-header">Catalog Criteria - {batchMatchCriteria.mrNumber}</div>
																<div className="card-body">
																	<div className="alert alert-warning" role="alert">
																		{batchMatchCriteria.ctlgCriteria ? batchMatchCriteria.ctlgCriteria : 'Catalog Criteria not available'}
																	</div>
																</div>
															</div>
														</div>

													</div>
												</div>
											)}
										</div>
									)}

									{activeTab === 'alertDetail' && showAlertDetailTab && (
										<div className="tab-pane fade show active">
											{alertDetail ? (
												<div className="card">
													<div className="card-header bg-info">{alertDetail.mrNumber}</div>
													<div className="card-body">
														<div className="row">
															<div className="col-md-6">
																<h5>Alert Detail</h5>
																<ul className="list-group">
																	<li className="list-group-item"><strong>Title:</strong> {alertDetail.alertTitle}</li>
																	<li className="list-group-item"><strong>Class:</strong> {alertDetail.alertClass}</li>
																	<li className="list-group-item"><strong>Source:</strong> {alertDetail.alertSource}</li>
																	<li className="list-group-item"><strong>Type:</strong> {alertDetail.alertType}</li>
																	<li className="list-group-item"><strong>Manufacturer:</strong> {alertDetail.manufacturer}</li>
																	<li className="list-group-item"><strong>Product Desc:</strong> <span dangerouslySetInnerHTML={{ __html: alertDetail.productDesc }} /></li>
																	<li className="list-group-item"><strong>Reason:</strong> <span dangerouslySetInnerHTML={{ __html: alertDetail.reason }} /></li>
																</ul>
															</div>
															<div className="col-md-6">
																{attachments && attachments.length > 0 ? (
																	<div>
																		<h5>Attachments</h5>
																		<ul className="list-group">
																			<ul className="list-group">
																				{attachments.map((file, idx) => (
																					<li className="list-group-item" key={idx}><span className='text-primary'>{file.clientId}</span>&nbsp;-&nbsp;
																						<a
																							href={`${config.API_BASE_URL}/attachment_download/${file.mongoId}`}
																							target="_blank"
																							rel="noopener noreferrer"
																							download={file.fileName}
																						>
																							{file.fileName}
																						</a>
																					</li>
																				))}
																			</ul>
																		</ul>
																	</div>
																) : (
																	<p>No attachments available.</p>
																)}
															</div>
														</div>
													</div>
												</div>
											) : (
												<div className="alert alert-info">No alert detail available.</div>
											)}
										</div>
									)}
								</div>
							</div>
						</div></div>

						{showDownloadModal && (
						  <div
						    className="modal-backdrop"
						    style={{
						      zIndex: 1050,
						      position: 'fixed',
						      top: 0,
						      left: 0,
						      width: '100vw',
						      height: '100vh',
						      background: 'rgba(0,0,0,0.3)'
						    }}
						  >
						    <div
						      style={{
						        zIndex: 1060,
						        position: 'fixed',
						        top: '50%',
						        left: '50%',
						        transform: 'translate(-50%, -50%)',
						        width: 300,
						        height: 'auto',
						        background: '#fff',
						        borderRadius: 8,
						        padding: '12px 16px',
						        boxShadow: '0 2px 16px rgba(0,0,0,0.15)',
						        textAlign: 'center'
						      }}
						    >
						      <div className="fw-bold">Please wait, downloading......</div>
						    </div>
						  </div>
						)}

					{renderStatusPopup()}
					<Footer />
				</div>
			</div>
		</>
	);
};

export default Home;